import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CASE_STUDIES, smSrc } from "../data/caseStudies";
import { Reveal } from "./Reveal";

const CaseRow = ({ study, index }) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  const reverse = index % 2 === 1;
  const isExternal = !!study.external;

  const open = () => {
    if (isExternal) {
      window.open(study.external, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/case-study/${study.id}`);
    }
  };

  return (
    <div
      ref={ref}
      className="group grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center cursor-hover"
      onClick={open}
      data-testid={`case-study-${study.id}`}
    >
      {/* Image */}
      <div className={`lg:col-span-7 ${reverse ? "lg:order-2" : ""}`}>
        <div className="relative overflow-hidden rounded-3xl aspect-[4/3] border border-charcoal/5 shadow-[0_30px_60px_-40px_rgba(34,34,34,0.4)]">
          <motion.img
            src={study.cover}
            srcSet={`${smSrc(study.cover)} 780w, ${study.cover} 1500w`}
            sizes="(max-width: 1024px) 100vw, 58vw"
            alt={study.title}
            loading="lazy"
            decoding="async"
            style={{ scale: imgScale, y: imgY }}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/10 transition-colors duration-500" />
          <div className="absolute top-5 left-5">
            <span className="backdrop-blur-md bg-cream/80 text-charcoal text-xs tracking-wide px-4 py-2 rounded-full border border-charcoal/5">
              {study.category}
            </span>
          </div>
        </div>
      </div>

      {/* Text */}
      <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
        <span className="font-heading text-6xl md:text-7xl text-charcoal/10 leading-none">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h3 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight text-charcoal group-hover:text-burgundy transition-colors duration-300">
          {study.title}
        </h3>
        <p className="mt-2 text-sm tracking-widest uppercase text-gold">{study.subtitle}</p>
        <p className="mt-5 text-base text-charcoal/70 leading-relaxed max-w-md">{study.summary}</p>

        {!isExternal && (
          <div className="mt-7 flex flex-wrap gap-6">
            {study.metrics.slice(0, 2).map((m) => (
              <div key={m.label}>
                <p className="font-heading text-2xl text-burgundy leading-none">{m.value}</p>
                <p className="text-xs text-charcoal/50 mt-1">{m.label}</p>
              </div>
            ))}
          </div>
        )}

        <button className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-charcoal group-hover:gap-3 transition-all">
          {isExternal ? "View on Behance" : "Read case study"}
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/20 group-hover:bg-burgundy group-hover:border-burgundy group-hover:text-cream transition-colors duration-300">
            <ArrowUpRight size={16} />
          </span>
        </button>
      </div>
    </div>
  );
};

export const CaseStudiesSection = () => {
  return (
    <section id="work" className="py-24 md:py-32 lg:py-40" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold text-gold">Selected Work</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-charcoal max-w-2xl">
              Case <span className="italic text-burgundy">Studies.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base text-charcoal/60 max-w-sm md:text-right">
              Every project starts with a business problem and ends with a measurable outcome.
              Here's how strategy meets craft.
            </p>
          </Reveal>
        </div>

        <div className="flex flex-col gap-24 md:gap-36">
          {CASE_STUDIES.map((study, i) => (
            <CaseRow key={study.id} study={study} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
